import { useEffect, useState } from 'react';
import {
  addClientIntoHeader,
  applicationLoadStatus,
  createHeaderInformation,
  loadOnePlaceScript,
  getAccessToken,
  changeSignoutUrl
} from '@/lib/oneplace.ts';
import Loading from './components/ui/loading';
import { UserProvider } from './components/Contexts/UserContext';
import { QueryData } from '@/components/Contexts/QueryContext';
import './index.css';
import AppRoutes from './routes/app.routes';
import { ENVIRONMENT } from '@/environments/environment.ts';
import { COOKIE_NAMES, LOCAL_STORAGE_NAMES } from '@/utils/constants.ts';
import { IClientCulture } from '@/model/IClientCulture';
import { IResponse } from '@/model/IResponse';
import { getSelectedCulture, loadTranslations, isTranslationsLoading, onTranslationsLoadingChange } from '@/utils/translations';
import { customJwtFetch } from './lib/customFetch';
import Cookies from 'js-cookie';
import { IUser } from '@/model/IUser.ts';
import useLocalStorage from '@/lib/use-local-storage';
import { TenantProvider, useTenant } from '@/contexts/TenantContext';
import UserNotFound from '@/pages/Layout/userNotFound.tsx';
import CustomErrorPage from '@/pages/Layout/customErrorPage.tsx';
import AccessRestricted from '@/pages/Errors/accessRestricted.tsx';
import React from 'react';
import { getValidationErrorData, setError } from '@/lib/ErrorHandler.ts';
import { useNotificationContext } from '@/components/Contexts/NotificationContext.tsx';
import { initCsrfService } from './lib/csrfService';
import { useTranslation } from 'react-i18next';

function AppContent() {
  const [applicationStatus, setApplicationStatus] = useState(applicationLoadStatus.Loading);
  const [user, setUser] = useState<IUser | undefined>(undefined);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [executeSignIn, setExecuteSignIn] = useState<boolean>(false);
  const { tenant, setTenant } = useTenant();
  const [, setCultures] = useLocalStorage<IClientCulture[]>(LOCAL_STORAGE_NAMES.CULTURES);
  const protectedRoutes = ['error', 'signout'];
  const [isAuthenticated, setIsAuthenticated] = useState<boolean>(false);
  const { joinClient } = useNotificationContext();
  const { ready: i18nReady } = useTranslation();
  const [translationsLoading, setTranslationsLoading] = useState<boolean>(isTranslationsLoading());

  // Track translation loading status
  useEffect(() => {
    return onTranslationsLoadingChange((isLoading) => {
      setTranslationsLoading(isLoading);
    });
  }, []);

  useEffect(() => {
    const getClientNameAndCheckPath = (pathname) => {
      const regex = /^\/([^/]+)(\/.*)?$/;
      const match = pathname.match(regex);
      if (match) {
        const clientName = match[1];
        const additionalPath = match[2];
        const hasAdditionalPath = additionalPath && additionalPath !== '/';
        return { clientName, hasAdditionalPath };
      }
      return { clientName: null, hasAdditionalPath: false };
    };

    setIsLoading(true);
    if (location.pathname) {
      const { clientName, hasAdditionalPath } = getClientNameAndCheckPath(location.pathname);
      const hasValidClientName = clientName && !protectedRoutes.includes(clientName);
      if (hasValidClientName) {
        if (clientName != tenant) {
          if (clientName == 'undefined' || clientName == 'null') {
            window.location.href = '/error/access-restricted';
            return;
          }
          setTenant(clientName);
          setExecuteSignIn(true);
        }
      }

      if (!hasAdditionalPath && hasValidClientName) {
        window.location.href = `/${clientName}/valuations`;
      }

      setIsLoading(false);
    }
  }, [location.pathname]);


  React.useEffect(() => {
    loadOnePlaceScript(setApplicationStatus);
  }, []);


  useEffect(() => {
    const getClientNameAndCheckPath = (pathname) => {
      const regex = /^\/([^/]+)(\/.*)?$/;
      const match = pathname.match(regex);
      if (match) {
        const clientName = match[1];
        const additionalPath = match[2];
        const hasAdditionalPath = additionalPath && additionalPath !== '/';
        return { clientName, hasAdditionalPath };
      }
      return { clientName: null, hasAdditionalPath: false };
    };

    const { clientName, hasAdditionalPath } = getClientNameAndCheckPath(location.pathname);
    const hasValidClientName = clientName && !protectedRoutes.includes(clientName);
    if (hasValidClientName) {
      if (clientName != tenant) {
        if (clientName == 'undefined' || clientName == 'null') {
          window.location.href = '/error/access-restricted';
          return;
        }
        setTenant(clientName);
        setExecuteSignIn(true);
      }
    }

    if (!hasAdditionalPath && hasValidClientName) {
      window.location.href = `/${clientName}/valuations`;
    }

    setIsLoading(false);
  }, [location.pathname]);


  useEffect(() => {
    const authenticationExpiration = Cookies.get(COOKIE_NAMES.SESSION_TIME);
    const isAuthenticationExpired = authenticationExpiration ? new Date(authenticationExpiration) < new Date() : true;

    if (user?.isBlocked) {
      setApplicationStatus(applicationLoadStatus.AccessRestricted);
      setIsAuthenticated(false);
      return;
    }

    if (!isAuthenticationExpired && user) {
      const clientName = user?.clientInfo?.clientName ?? '';
      const shortName = user?.clientInfo?.shortname ?? '';

      addClientIntoHeader(clientName);
      changeSignoutUrl(shortName);
      setIsAuthenticated(true);
    } else {
      setExecuteSignIn(true);
      setIsAuthenticated(false);
    }
  }, [user]);

  useEffect(() => {
    if (isAuthenticated && tenant) {
      const connect = async () => {
        await joinClient(tenant);
      };

      connect();
    }
  }, [tenant, isAuthenticated]);

  const culturesQuery = QueryData<IResponse<IClientCulture[]>>({
    url: `${ENVIRONMENT.authApi}/culture/GetAllCultures`,
    enabled: applicationStatus === applicationLoadStatus.Finished,
    queryKey: ['CULTURES_APPLICATION']
  });

  useEffect(() => {
    const fetchCulture = async () => {
      if (culturesQuery.isFetched && culturesQuery.isSuccess) {
        const culturesResponse = culturesQuery.data.data;
        setCultures(culturesResponse);
        const selectedCulture: IClientCulture = getSelectedCulture(culturesResponse);
        const forceUpdate = true;
        const translationSuccessfulLoaded = await loadTranslations(selectedCulture.id, forceUpdate);

        if (!translationSuccessfulLoaded) {
          const errorData = getValidationErrorData('An error occurred while loading translations');
          setError(errorData);
          setApplicationStatus(applicationLoadStatus.Error);
        }

        const clientName = user?.clientInfo?.clientName ?? '';
        await createHeaderInformation(culturesResponse, selectedCulture);
      }
    };

    fetchCulture();

  }, [culturesQuery.isFetched]);

  useEffect(() => {
    const handleAuthentication = async () => {
      try {
        setIsLoading(true);

        // Obter o token JWT para fazer login
        const jwtToken = await getAccessToken();
        if (!jwtToken) {
          console.error('Falha ao obter token JWT para autenticação');
          setApplicationStatus(applicationLoadStatus.AccessRestricted);
          setIsLoading(false);
          return;
        }

        // Obter o cliente selecionado
        const client = tenant || '';
        const loginEndpoint = `${ENVIRONMENT.baseUrlIcarus}/Authorization/login?ClientShortName=${client}`;

        // Usar customJwtFetch para fazer login com JWT e proteção CSRF
        const response = await customJwtFetch<{ accessToken: string } & IUser>(
          loginEndpoint,
          {
            method: 'POST',
            // Não enviar body, pois os parâmetros agora estão na URL
          },
          jwtToken
        );

        // Usamos o objeto de resposta como nosso usuário
        const userData = response as IUser;
        setUser(userData);
        Cookies.set(COOKIE_NAMES.SESSION_TIME, userData?.expiration);
        setIsLoading(false);
        setExecuteSignIn(false);
      } catch (error) {
        console.error(error);
        setApplicationStatus(applicationLoadStatus.AccessRestricted);
        setIsLoading(false);
      }
    };

    const hasValidTenant =
      !!tenant?.trim() &&
      !protectedRoutes.some(route => location.pathname.startsWith(route));
    const authenticationExpiration = Cookies.get(COOKIE_NAMES.SESSION_TIME);
    const isAuthenticationExpired = authenticationExpiration ? new Date(authenticationExpiration) < new Date() : true;
    const shouldAuthenticate = !authenticationExpiration || isAuthenticationExpired || user === null || executeSignIn;
    if (isAuthenticationExpired) {
      Cookies.remove(COOKIE_NAMES.SESSION_TIME);
    }

    if (shouldAuthenticate && hasValidTenant && applicationStatus === applicationLoadStatus.Finished) {
      handleAuthentication();
    } else {
      setIsLoading(false);
    }
  }, [tenant, applicationStatus, executeSignIn]);

  // Inicializar serviço CSRF quando o componente montar
  useEffect(() => {
    initCsrfService();
  }, []);

  return (
    <UserProvider user={user} setUser={setUser}>
      {(() => {
        if (isLoading || applicationStatus === applicationLoadStatus.Loading) {
          return <Loading message="Loading application..." />;
        }
        if (translationsLoading || !i18nReady) {
          return <Loading message="Loading translations..." />;
        }
        if (applicationStatus === applicationLoadStatus.UserNotFound) {
          return <UserNotFound />;
        }
        if (applicationStatus === applicationLoadStatus.AccessRestricted) {
          return <AccessRestricted />;
        }
        if (applicationStatus === applicationLoadStatus.Error) {
          return <CustomErrorPage />;
        }
        return <AppRoutes />;
      })()}
    </UserProvider>
  );
}

export default function App() {
  return (
    <TenantProvider>
      <AppContent />
    </TenantProvider>
  );
}
